import { ChangeEvent, Fragment, useRef, useState } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  GrBox,
  GrButton,
  GrDialog,
  GrDialogProps,
  GrRadioGroup,
  GrSelect,
  GrStack,
  GrTextField,
  GrTypography,
  palette,
} from "@jnpmedi/grebob";
import SignaturePad from "react-signature-pad-wrapper";
import { DrawingToolbar } from "@newComponents/common/DrawingToolbar";
import { DocumentSignerSignatureExt, SignatureType } from "@newComponents/common/Viewer/types";
import { getSignReasonMessagesForGrSelect } from "@newComponents/common/Viewer/Viewer.function";
import trimCanvas from "@newLibs/canvas/trimCanvas";
import { isInvalidFileType } from "@newLibs/converting";
import { isFileSizeExceedsLimit, isMobileDevice, langHelper, MvnMessage } from "@newLibs/utils";
import { useBoolean, UseBoolean } from "@newHooks/common/useBoolean";
import { useDrawingRefTrace } from "@newHooks/common/useDrawingRefTrace";
import { notify } from "@newHooks/context/snackbar/notify";
import { useCreateDocumentSignerPresignedUrlForUpload } from "@newHooks/mutation/common/createDocumentSignerPresignedUrl.mutation";
import { useCreateEmailVerificationCodeMutation } from "@newHooks/mutation/common/createEmailVerificationCode.mutation";
import { useValidateDocumentSignerStampImage } from "@newHooks/mutation/common/validateDocumentSignerStampImage.mutation";
import { useVerifyVerificationCodeMutation } from "@newHooks/mutation/common/verifyVerificationCode.mutation";
import baseMedia from "@assets/theme/baseDefine/baseMedia";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { AUTH_USER_VERIFICATION_CODE_LANG } from "@maven-msg/lang/auth";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { DOCS_DOCUMENT_LANG, DOCS_FOLDER_LANG } from "@maven-msg/lang/docs";
import { DOCS_COMMON_LANG } from "@maven-msg/lang/docs/common";
import { MvnLanguage } from "@maven-msg/lib/Message";
import { LoLang } from "@maven-rest/common/common.types";
import { DocumentSign } from "@maven-rest/docs/DocumentSign.dto";
import { RefResourceType } from "@maven-rest/ref/Ref.dto";
import { convertFileTypeToS3ResourceContentType } from "@maven-rest/resource/S3.lib";

const SIGN_PAD_IMAGE_MAX_WIDTH = 96;
const SIGN_PAD_IMAGE_MAX_HEIGHT = 28;

const SIGN_STAMP_IMAGE_MAX_WIDTH = 96;
const SIGN_STAMP_IMAGE_MAX_HEIGHT = 28;

type FileS3UploadResultType = {
  fileKey: string;
  fileName: string;
  fileBucket: string;
  previewUrl: string;
  downloadUrl: string;
};

interface SignatureDialogProps {
  open: UseBoolean;
  documentSign: DocumentSign;
  signatures: DocumentSignerSignatureExt[];
  setSignatures: (val: DocumentSignerSignatureExt[]) => void;
  signingReason: string;
  signingReasonAttrUuid: string;
  setSigningReason: (val: string) => void;
  setSigningReasonAttrUuid: (val: string) => void;
  signingSignatureAttrUuid: string;
  signerEmail: string;
  signerIndex: number;
  token: string;
  accessToken?: string;
  size?: GrDialogProps["size"];
  onDialogClose: (signatures?: DocumentSignerSignatureExt[]) => void;
}

export function SignaturePadDialog({
  open,
  documentSign,
  signatures,
  setSignatures,
  signingReason,
  signingReasonAttrUuid,
  setSigningReason,
  setSigningReasonAttrUuid,
  signingSignatureAttrUuid,
  signerEmail,
  signerIndex,
  token,
  accessToken,
  size = "xs",
  onDialogClose,
}: SignatureDialogProps) {
  const loLang = langHelper.getLang();

  const [verificationUuid, setVerificationUuid] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const isSigningReasonDirectInput = useBoolean(false);
  const isS3ObjectUploading = useBoolean(false);
  const [signatureType, setSignatureType] = useState<SignatureType>(SignatureType.Sign);
  const [selectedSignReasonOption, setSelectedSignReasonOption] = useState<string>("");
  const [signImageUploadResults, setSignImageUploadResults] = useState<FileS3UploadResultType | null>(null);

  const isEmailVerificationRequested = useRef(false);
  const isEmailVerified = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<File | null>(null);
  const fileS3UploadResultRef = useRef<FileS3UploadResultType | null>(null);

  const uploadSizeLimitInMb = 2;
  const isDisabledConfirm =
    documentSign.isGxpCompatible && (!signingReason.trim().length || (!!verificationUuid && !verificationCode.trim().length));

  const {
    signaturePadRef,
    isSignatureEmpty,
    isRedoStackEmpty,
    isFirstDrawingStarted,
    onClickUndo,
    onClickRedo,
    onClickSignatureDownload,
    onClickClear: handleDrawingClear,
  } = useDrawingRefTrace(signatureType);

  const { mutate: createEmailVerificationCodeRequest } = useCreateEmailVerificationCodeMutation({
    onSuccess: (data) => {
      setVerificationUuid(data.userVerificationCode.uuid);
      notify(MvnMessage(AUTH_USER_VERIFICATION_CODE_LANG.USER_VERIFICATION_CODE_EMAIL_SEND_SUCC), "SUCC");
    },
    onError: (error) =>
      notify(
        MvnMessage(AUTH_USER_VERIFICATION_CODE_LANG.USER_VERIFICATION_CODE_EMAIL_SEND_FAIL, { message: error.responseMessage }),
        "WARN"
      ),
  });

  const { mutate: verifyVerificationCodeRequest } = useVerifyVerificationCodeMutation({
    onSuccess: () => {
      isEmailVerified.current = true;
      signatureType === SignatureType.Sign ? enterSignature() : enterStamp();
    },
    onError: (error) => {
      notify(
        MvnMessage(AUTH_USER_VERIFICATION_CODE_LANG.USER_VERIFICATION_CODE_EMAIL_VERIFY_FAIL, { message: error.responseMessage }),
        "WARN"
      );
    },
  });

  const { mutate: createDocumentSignerPresignedUrlForUploadRequest } = useCreateDocumentSignerPresignedUrlForUpload({
    onSuccess: (data) => {
      if (fileRef.current) {
        const xhr = new XMLHttpRequest();
        const { key, bucket, previewUrl, downloadUrl, url } = data.presignedUrl;

        xhr.open("put", url, true);
        xhr.setRequestHeader("content-type", fileRef.current.type);
        xhr.onload = () => {
          if (xhr.status === 200) {
            fileS3UploadResultRef.current = {
              fileKey: key,
              fileBucket: bucket,
              fileName: fileRef.current!.name,
              previewUrl,
              downloadUrl,
            };

            validateDocumentSignerStampImageRequest({ fileKey: key, fileBucket: bucket });
          } else {
            notify(MvnMessage("the file upload request has been failed."), "ERROR");
          }
        };

        xhr.onerror = () => {
          notify(MvnMessage("the file upload request has been failed."), "ERROR");
          isS3ObjectUploading.onFalse();
        };

        xhr.onloadend = () => isS3ObjectUploading.onFalse();
        xhr.send(fileRef.current);
      }
    },
    onError: (error) => {
      fileRef.current = null;
      isS3ObjectUploading.onFalse();
      notify(MvnMessage("The file upload request has been failed."), "ERROR");
    },
  });

  const { mutate: validateDocumentSignerStampImageRequest, isLoading: validateDocumentSignerStampImageLoading } =
    useValidateDocumentSignerStampImage({
      onSuccess: (data) => {
        if (data.result) {
          // NOTE: BE측으로부터 PDF mebedding이 가능한 이미자 파일인지 여부 확인 후 상태변경
          setSignImageUploadResults(fileS3UploadResultRef.current);
        }
      },
      onError: (error) => {
        notify(error.responseMessage, "WARN");
      },
    });

  const enterSignature = () => {
    const isSignatureImageEmpty = signaturePadRef.current?.isEmpty();

    if (typeof isSignatureImageEmpty !== "boolean" || isSignatureImageEmpty) {
      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_SIGNATURE_MISSING), "WARN");
      return;
    }

    if (documentSign.isGxpCompatible) {
      if (isEmailVerificationRequested.current) {
        if (!isEmailVerified.current) {
          if (verificationCode) {
            verifyVerificationCodeRequest({
              uuid: verificationUuid,
              code: verificationCode,
            });
            return;
          }
        }
      } else {
        isEmailVerificationRequested.current = true;
        createEmailVerificationCodeRequest({ email: signerEmail });
        return;
      }
    }

    const canvas = signaturePadRef.current?.canvas.current!;

    // TODO: scale up 작업을 통해 서명이 안깨지도록 수정 필요
    const canvasCopy = document.createElement("canvas");
    canvasCopy.width = canvas.width;
    canvasCopy.height = canvas.height;
    canvasCopy.getContext("2d")!.drawImage(canvas, 0, 0);

    const signatureImage = trimCanvas(canvasCopy);
    if (signatureImage) {
      let width = signatureImage.width;
      let height = signatureImage.height;

      // 비율을 유지하면서 최대 크기 적용
      const widthRatio = SIGN_PAD_IMAGE_MAX_WIDTH / width;
      const heightRatio = SIGN_PAD_IMAGE_MAX_HEIGHT / height;
      const ratio = Math.min(widthRatio, heightRatio);

      if (ratio < 1) {
        width = width * ratio;
        height = height * ratio;
      }

      const newSignatures = signatures
        .filter((e) => e.attrUuid !== signingSignatureAttrUuid)
        .concat([
          {
            attrUuid: signingSignatureAttrUuid,
            signature: signatureImage.toDataURL("image/png")!,
            verificationUuid,
            verificationCode,
            width,
            height,
          },
        ]);

      setSignatures(newSignatures);
      handleDialogClose(newSignatures);
    } else {
      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_FAILED_IDENTIFY_SIGNATURE), "WARN");
      clearAllStatus();
    }
  };

  const enterStamp = async () => {
    if (!fileRef.current) {
      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_SIGNATURE_MISSING), "WARN");
      return;
    }

    if ((await fileRef.current.arrayBuffer()).byteLength === 0) {
      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ZEROBYTES_IMAGE), "WARN");
      return;
    }

    if (documentSign.isGxpCompatible) {
      if (isEmailVerificationRequested.current) {
        if (!isEmailVerified.current) {
          if (verificationCode) {
            verifyVerificationCodeRequest({
              uuid: verificationUuid,
              code: verificationCode,
            });
            return;
          }
        }
      } else {
        isEmailVerificationRequested.current = true;
        createEmailVerificationCodeRequest({ email: signerEmail });
        return;
      }
    }

    const reader = new FileReader();

    reader.onload = function (event) {
      const stampImage = new Image();

      stampImage.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          console.error("Unable to get 2D context");
          return;
        }

        canvas.width = stampImage.width;
        canvas.height = stampImage.height;

        ctx.drawImage(stampImage, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        const newCanvas = document.createElement("canvas");
        const newCtx = newCanvas.getContext("2d");

        if (!newCtx) {
          console.error("Unable to get 2D context for new canvas");
          return;
        }

        newCanvas.width = canvas.width;
        newCanvas.height = canvas.height;

        newCtx.putImageData(imageData, 0, 0);

        const stampBase64 = newCanvas.toDataURL();

        if (signImageUploadResults && stampBase64) {
          let width = newCanvas.width;
          let height = newCanvas.height;
          const { fileKey: key, fileBucket: bucket } = signImageUploadResults;

          // 비율을 유지하면서 최대 크기 적용
          const widthRatio = SIGN_STAMP_IMAGE_MAX_WIDTH / width;
          const heightRatio = SIGN_STAMP_IMAGE_MAX_HEIGHT / height;
          const ratio = Math.min(widthRatio, heightRatio);

          if (ratio < 1) {
            width = width * ratio;
            height = height * ratio;
          }

          const stampSignature: DocumentSignerSignatureExt = {
            attrUuid: signingSignatureAttrUuid,
            signature: { key, bucket, stampBase64 },
            verificationUuid,
            verificationCode,
            width,
            height,
          };

          const newSignatures = signatures.filter((e) => e.attrUuid !== signingSignatureAttrUuid).concat(stampSignature); // 화면 레이어 표시를 위해 둘다사용

          setSignatures(newSignatures);
          handleDialogClose(newSignatures);
        } else {
          notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_FAILED_IDENTIFY_SIGNATURE), "WARN");
        }
      };
      if (event.target && typeof event.target.result) {
        stampImage.src = event.target.result as string;
      }
    };

    reader.readAsDataURL(fileRef.current);
  };

  const clearAllStatus = () => {
    clearExceptSigningReason();

    setSelectedSignReasonOption("");
    if (signingReasonAttrUuid === signingSignatureAttrUuid) {
      setSigningReason("");
      setSigningReasonAttrUuid("");
    }
  };

  const clearExceptSigningReason = () => {
    handleDrawingClear();
    isSigningReasonDirectInput.onFalse();

    setVerificationUuid("");
    setVerificationCode("");

    isEmailVerified.current = false;
    isEmailVerificationRequested.current = false;

    setSignImageUploadResults(null);
    fileRef.current = null;
    fileS3UploadResultRef.current = null;
  };

  const handleDialogConfirm = () => {
    if (isDisabledConfirm) {
      return;
    }

    signatureType === SignatureType.Sign ? enterSignature() : enterStamp();
  };

  const handleDialogClose = (signatures?: DocumentSignerSignatureExt[]) => {
    setSignatureType(SignatureType.Sign);
    clearExceptSigningReason();
    onDialogClose(signatures);
  };

  const handleTextfieldEnterKey: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === "Enter") {
      handleDialogConfirm();
    }
  };

  const handleValueChangeEsignReason = (value: string | null) => {
    if (!value) {
      setSigningReason("");
      setSelectedSignReasonOption("");
      setSigningReasonAttrUuid("");
      return;
    }

    setSelectedSignReasonOption(value);
    setSigningReasonAttrUuid(signingSignatureAttrUuid);
    if (value === MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_OTHER)) {
      isSigningReasonDirectInput.onTrue();
      setSigningReason("");
    } else {
      isSigningReasonDirectInput.onFalse();
      setSigningReason(value);
    }
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    (() => {
      const files = event.currentTarget.files;

      if (!files || !files.length) {
        notify(MvnMessage(DOCS_COMMON_LANG.EMPTY_FILE_WARN), "WARN");
        return;
      }

      const file = files[0];

      if (file) {
        if (isFileSizeExceedsLimit(file, uploadSizeLimitInMb)) {
          notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_EXCEEDS_MAX_SIZE, { limit: uploadSizeLimitInMb }), "WARN");
          return;
        }

        if (isInvalidFileType(file, ".jpeg,.jpg,.png")) {
          notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_INVALID_EXTENSION, { ext: file.type.toLowerCase() }), "WARN");
          return;
        }

        fileRef.current = file;
        isS3ObjectUploading.onTrue();

        createDocumentSignerPresignedUrlForUploadRequest({
          token,
          name: file.name,
          resourceType: RefResourceType.Docs_FolderFile,
          contentType: convertFileTypeToS3ResourceContentType(file.type.toLowerCase()),
          accessToken: accessToken || "",
        });
      }
    })();

    event.currentTarget.value = "";
  };

  const renderSignaturePad = () => {
    return (
      <GrStack key="document-sign-user-draw-signature-pad" flexDirection="column" gap={8}>
        <SignatureStackWrapper
          key="drawSignature"
          border={`1px solid ${palette["grey025"]}`}
          alignItems="center"
          justifyContent="center"
          borderRadius="8px"
          width="100%"
          height={baseHeight.appSignaturePadHeight}
        >
          <SignaturePadWrapper loLang={loLang} hidePlaceholder={isFirstDrawingStarted}>
            <SignaturePad
              ref={signaturePadRef}
              options={{
                penColor: "black",
                minWidth: 3,
                maxWidth: 5,
                dotSize: 3,
              }}
              canvasProps={{ className: "app-docs-document--signature-canvas" }}
            />
          </SignaturePadWrapper>
        </SignatureStackWrapper>
        <DrawingToolbar
          isDownloadable
          drawDownloadButtonDisabled={isSignatureEmpty}
          undoDisabled={isSignatureEmpty}
          redoDisabled={isRedoStackEmpty}
          onClickUndo={onClickUndo}
          onClickRedo={onClickRedo}
          onClickDrawDownload={onClickSignatureDownload}
        />
      </GrStack>
    );
  };

  const renderStampSignatureUploader = () => {
    return (
      <Fragment key="document-sign-user-stamp-image-uploader">
        {!signImageUploadResults?.fileKey && (
          <GrStack
            border={`1px solid ${palette["grey025"]}`}
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            borderRadius="8px"
            width="100%"
            height={baseHeight.appSignaturePadHeight}
            backgroundColor={palette["bg020"]}
            gap={12}
          >
            <GrStack flexDirection="column" gap={6} alignItems="center">
              <GrTypography type="Content.Subtitle02" color="grey060">
                {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_STAMP_DESCRIPTION)}
              </GrTypography>
              <GrTypography type="Content.Body04" color="grey060">
                {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_DESCRIPTION, {
                  num: uploadSizeLimitInMb,
                  type: "JPEG, JPG, PNG ",
                })}
              </GrTypography>
            </GrStack>
            <GrButton
              isLoading={isS3ObjectUploading.value || validateDocumentSignerStampImageLoading}
              onClick={() => fileInputRef.current?.click()}
            >
              {MvnMessage("Browse")}
            </GrButton>
            <input ref={fileInputRef} type="file" accept=".jpeg,.jpg,.png" hidden onChange={handleFileInputChange} />
          </GrStack>
        )}
        {!!signImageUploadResults?.fileKey && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <img
              style={{ maxHeight: baseHeight.appSignaturePadHeight, maxWidth: "100%" }}
              src={signImageUploadResults.previewUrl}
              alt="File Preview"
            />
          </div>
        )}
      </Fragment>
    );
  };

  return (
    <GrDialog
      className="app-docs-document-sign-signature-dialog"
      title={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_ENTER_BUTTON)}
      onClose={handleDialogClose}
      onConfirm={handleDialogConfirm}
      confirmButton={{
        children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_OK_NOUN),
        disabled: isDisabledConfirm,
      }}
      cancelButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CANCEL_NOUN) }}
      open={open.value}
      footer={
        <GrButton startIcon="eraser" variant="outlined" color="grey" onClick={clearAllStatus}>
          {MvnMessage(COMMON_LANG.CLEAR_ALTER)}
        </GrButton>
      }
      size={isMobileDevice() ? "mobile" : size}
      preventCloseOnOutsideClick
    >
      <GrStack flexDirection="column" gap={8} height="100%" minHeight="182px">
        <RadioGroupWrapper>
          <GrRadioGroup
            key="signature-dialog-radio-group"
            direction="row"
            options={[
              {
                label: { text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_SIGNATURE_ENTER), htmlFor: "sign-type-radio" },
                value: SignatureType.Sign,
              },
              {
                label: { text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_STAMP_ENTER), htmlFor: "stamp-type-radio" },
                value: SignatureType.Stamp,
              },
            ]}
            value={signatureType}
            onChange={(_, newValue) => {
              newValue && setSignatureType(newValue);
              handleDrawingClear();
            }}
          />
        </RadioGroupWrapper>

        {signatureType === SignatureType.Sign && renderSignaturePad()}
        {signatureType === SignatureType.Stamp && renderStampSignatureUploader()}

        {documentSign.isGxpCompatible && (
          <>
            {(!signingReasonAttrUuid || signingReasonAttrUuid === signingSignatureAttrUuid) && (
              <GrSelect
                options={getSignReasonMessagesForGrSelect(documentSign, signerIndex)}
                label={{ text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_LABEL), style: { marginTop: "8px" } }}
                placeholder={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_SELECT_PLACEHOLDER)}
                required
                fullWidth
                value={selectedSignReasonOption}
                onChange={(_, value) => handleValueChangeEsignReason(value)}
              />
            )}
            {isSigningReasonDirectInput.value && (
              <GrTextField
                placeholder={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_REASON_PLACEHOLDER)}
                value={signingReason}
                maxLength={50}
                autoFocus
                fullWidth
                required
                onKeyDown={handleTextfieldEnterKey}
                onChange={(event) => setSigningReason(event.currentTarget.value)}
              />
            )}
            {!!verificationUuid && (
              <GrTextField
                label={{ text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_EMAIL_VERIFICATION_LABEL), style: { marginTop: "8px" } }}
                placeholder={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_EMAIL_VERIFICATION_PLACEHOLDER, {
                  email: signerEmail,
                })}
                type="number"
                maxLength={8}
                fullWidth
                autoFocus
                required
                onKeyDown={handleTextfieldEnterKey}
                value={verificationCode}
                onChange={(event) => setVerificationCode(event.currentTarget.value)}
              />
            )}
          </>
        )}
      </GrStack>
    </GrDialog>
  );
}

const SignatureStackWrapper = styled(GrStack)`
  position: relative;
  box-sizing: border-box;
`;

const SignaturePadWrapper = styled(GrBox)<{ loLang: LoLang; hidePlaceholder?: boolean }>`
  width: 100%;
  height: 100%;

  &:before {
    content: ${({ loLang }) =>
      `"${MvnMessage(
        DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_SIGNATURE_PAD_DESC,
        undefined,
        undefined,
        loLang === LoLang.KO ? MvnLanguage.KO : MvnLanguage.EN
      )}"`};
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(${baseHeight.appSignaturePadHeight} - 2px);
    border-radius: 7px;
    background: #f4f6f9;
    color: #697077;
    line-height: ${baseHeight.appSignaturePadHeight};
    letter-spacing: 5px;
    text-align: center;
    text-indent: 0;
  }

  ${({ hidePlaceholder }) =>
    hidePlaceholder &&
    css`
      &:before {
        content: none;
        display: none;
      }
    `}

  .app-docs-document--signature-canvas {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 8px;
  }
`;

const RadioGroupWrapper = styled(GrStack)`
  .MuiFormGroup-root {
    gap: 8px;
  }
  .GrRadio-wrapper {
    width: 166px;
  }

  ${baseMedia.mobile} {
    user-select: none;

    .GrRadio-wrapper {
      width: 116px;
    }
  }
`;
